import * as React from 'react';

import PostType from '@research-api/utils/api/typings/PostType';

import FeaturedPodcast from '@/components/pages/frontpage/components/Feature/Right/FeaturedPodcast';
import KeyMetrics from '@/components/pages/frontpage/components/Feature/Right/KeyMetrics';
import Newsletter from '@/components/pages/frontpage/components/Feature/Right/Newsletter';
import ResearchTeaser from '@/components/pages/frontpage/components/Feature/Right/ResearchTeaser';
import subscribeToNewsletter from '@/utils/functions/subscribeToNewsletter';
import type Podcast from '@/types/pages/api/podcasts/Podcast';
import { Webinar } from '@/types/ArticleType';
import FeaturedWebinar from '@/components/pages/frontpage/components/Feature/Right/FeaturedWebinar';

type SectionDefinition = {
    title: string;
    children?: React.ReactElement;
};

type RightProps = {
    keyMetrics: SectionDefinition;
    keyMetricSponsor: string;
    featuredResearchPost: PostType | null;
    featuredPodcast?: Podcast | null;
    featuredWebinar?: Webinar | null;
};

const Right = (props: RightProps) => {
    const { keyMetrics, featuredResearchPost, featuredPodcast, featuredWebinar, keyMetricSponsor } = props;

    return (
        <div className="lg:flex lg:flex-row xl:flex-col border-t border-gray-200 xl:border-0 items-baseline self-stretch">
            <div className="flex flex-col md:flex-row lg:flex-col bg-white border-gray-200 w-full border-b">
                <KeyMetrics
                    title={keyMetrics.title}
                    sponsor={keyMetricSponsor}
                    className=" md:w-1/2 lg:w-full md:border-r lg:border-r-0"
                />
                <Newsletter
                    title="Newsletter"
                    subTitle="Blockworks Daily"
                    description="Get the daily newsletter that helps thousands of investors understand the markets."
                    inputPlaceholder="Email address"
                    buttonText="subscribe"
                    onButtonClick={subscribeToNewsletter}
                    className="border-t md:border-0 lg:border-t md:w-1/2 lg:w-full"
                />
            </div>
            <div className="flex flex-col md:flex-row lg:flex-col justify-start self-stretch bg-white border-l xl:border-l-0 w-full">
                {featuredResearchPost && (
                    <ResearchTeaser
                        headerTitle="Research"
                        slug={featuredResearchPost.slug || ''}
                        title={featuredResearchPost.title || ''}
                        image={featuredResearchPost.featureImage ?? null}
                        description={featuredResearchPost.excerpt}
                        authorTitle={featuredResearchPost.primaryAuthor?.name || 'Research Team'}
                        authorUrl={`https://www.blockworksresearch.com/team/${
                            featuredResearchPost.primaryAuthor?.slug || ''
                        }`}
                        publishedAt={featuredResearchPost.publishedAtTimestamp || 0}
                        className="md:w-1/2 lg:w-full p-10"
                    />
                )}

                {featuredPodcast && (
                    <FeaturedPodcast
                        title="featured podcast"
                        podcastTitle=""
                        podcastUrl={`/podcast/${featuredPodcast.slug}`}
                        podcastShowTitle={`${featuredPodcast.title}`}
                        podcastShowDescription={`${featuredPodcast.summary}`}
                        podcastShowUrl=""
                        rssUrl={featuredPodcast.feedUrl}
                        itunesUrl={featuredPodcast.itunesIdentifier}
                        spotifyUrl={featuredPodcast.spotifyIdentifier}
                        youtubeUrl=""
                        className="border-t md:border-t-0 lg:border-t border-b-0 md:w-1/2 lg:w-full"
                    />
                )}

                {featuredWebinar && (
                    <FeaturedWebinar
                        title={featuredWebinar.title}
                        smallHeadline="webinar"
                        url={`/webinar/${featuredWebinar.slug}`}
                        showMoreUrl={`/webinar/${featuredWebinar.slug}`}
                        description={featuredWebinar.excerpt}
                        sponsoredBy={featuredWebinar.customFields?.sponsored_by}
                        className="border-l lg:border-l-0 border-t md:border-t-0 lg:border-t border-b-0 md:w-1/2 lg:w-full"
                    />
                )}
            </div>
        </div>
    );
};

export default Right;
